import React, { useContext, useState, useEffect, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isNull from 'lodash/isNull'
import { SEARCH_NAME_QUERY } from '../../components/graphql/BabyNameQuery'
import Layout from '../Layout'
import PropTypes from 'prop-types'
import { DetectDeviceContext } from 'wsc/components/context/DetectDeviceProvider'
import DocumentHead from '../DocumentHead'
import { MEDIA, PADDINGS, FONT_FAMILIES } from '../../utils/styles'
import styled from 'styled-components/macro'
import PortionLayout from '../PortionLayout'
import { ReactComponent as SearchIcon } from '../../statics/images/babyname/icon-search.svg'
import { ReactComponent as ShareIcon } from '../../statics/images/babyname/icon-share-arrow.svg'
// import TrendingStories from './TrendingStories'
import GridList from '../GridList'
import NameButton from './NameButton'
import SearchModal from './SearchModal'
import { SEX_CONFIGS } from '../../configs/babyname'
import SearchWidget from './SearchWidget'
import StickyButton from './StickyButton'
import { webShareAPI } from 'wsc/utils/socialShare'
import AdSlot, { ScAdSlotLeader, ScAdSlotRightRail } from 'wsc/components/AdSlot'
import AdFooter from 'wsc/components/AdFooter'
import { getOgURL } from '../../utils/url'
import LoadingScreen from '../LoadingScreen'
import { Redirect } from 'react-router-dom'
import routes from '../../configs/routes'
import client from '../../utils/initStrapi'
// import RecommendedPosts from '../post/RecommendedPosts'

const ScFlexWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 50px;
  margin-bottom: 35.7px;
  ${MEDIA.MOBILE`
    margin-top: 30px;
    margin-bottom: 30.3px;
  `}
  ${MEDIA.TABLET`
    margin-top: 40px;
  `}
`
const ScTitle = styled.h1`
  font-family: ${FONT_FAMILIES.SERIF};
  margin-bottom: 12px;
  font-size: 1.43rem;
  font-weight: normal;
  line-height: 1.3;
  ${MEDIA.TABLET`
    font-size: 1.87rem;
  `}
  ${MEDIA.DESKTOP`
    font-size: 2.18rem;
    line-height: 1.34;
  `}
`
const ScSearchWrapper = styled.div`
  margin-bottom: 50px;
  ${MEDIA.TABLET`
    margin-bottom: 40px;
  `}
`
const ScSearchResultLoadingScreenWrapper = styled.div`
  position: relative;
  width: 100%
  min-height: 400px;
  ${MEDIA.MOBILE`
    min-height: 250px;
  `}
`
const ScStickyFlex = styled.div`
  position: fixed;
  z-index: 2;
  display: flex;
  flex-direction: column;
  bottom: 132px;
  ${MEDIA.MOBILE`
    right: 0;
    padding-right: ${PADDINGS.DEFAULT.MOBILE}px;
  `}
  ${MEDIA.TABLET`
    bottom: 32px;
    margin-left: 556px;
  `}
`
const ScContentAside = styled.aside`
  align-self: flex-start;
  height: 100%;
`
const ScSticky = styled.div`
  position: sticky;
  top: 75px;
`
// const ScTrendingWrapper = styled.div`
//   margin-top: 38px;
//   ${MEDIA.TABLET`
//     margin-top: 48px;
//   `}
//   ${MEDIA.DESKTOP`
//     width: ${PAGE_WIDTHS.DESKTOP}px;
//     margin-top: 58px;
//   `}
// `
const ScNameGridList = styled(GridList)`
  margin-bottom: 12px;
`
const ScBGLoadingScreenWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  background-color: white;
`

const ResultNameListRender = ({ text, sex, isNeutral }) => {
  const { isMobile } = useContext(DetectDeviceContext)
  const [isLoading, setIsLoading] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [isReset, setIsReset] = useState(false)
  const [isNotFound, setIsNotFound] = useState(false)
  const [ref, inView] = useInView({ rootMargin: '0px 0px 1000px 0px' })
  const debounce = useRef(false)
  const nameResult = useRef([])
  const offset = useRef(0)
  const nameIndex = useRef(0)
  const nameComponents = useRef([])

  const namePerRow = isMobile ? 1 : 2
  const pmpIndex = namePerRow * 5
  const inContentIndex = namePerRow * 8
  const limit = 100

  let gridList = []
  let gridIndex = 1

  useEffect(() => {
    // Reset state when the page was changed
    setIsLoading(false)
    setIsComplete(false)
    setIsReset(true)
    setIsNotFound(false)
    offset.current = 0
    nameResult.current = []
    nameIndex.current = 0
    nameComponents.current = []
  }, [text, sex, namePerRow])

  const searchParams = isNull(isNeutral)
    ? {
        text: text,
        sex: sex,
      }
    : {
        text: text,
        sex: sex,
        genderNeutral: isNeutral,
      }

  const renderedName = (
    <>
      <ScTitle>
        {isNotFound ? 'No match found for' : 'Results for'} ‘{text}’
      </ScTitle>
      <AdSlot type="in_content" tagless lazy={false} />
      {nameResult.current}
      <div ref={ref}></div>
    </>
  )

  useEffect(() => {
    const fetchName = async () => {
      setIsLoading(true)

      try {
        const { data, errors } = await client.query({
          query: SEARCH_NAME_QUERY,
          variables: { ...searchParams, offset: offset.current * limit, limit: limit },
          errorPolicy: 'all',
        })

        if (errors) return <Redirect to={routes.error.path} />

        const names = get(data, 'searchNames.names', [])

        if (isEmpty(names)) {
          setIsNotFound(true)
          setIsComplete(true)
        } else {
          names.forEach((item, index) => {
            nameComponents.current.push(
              <NameButton
                key={`searchName-${item.id}-${nameIndex}`}
                id={item.id}
                name={item.name}
                sex={item.sex}
                defaultStateColor={SEX_CONFIGS[item.sex].nameButton.defaultStateColor}
                activeStateColor={SEX_CONFIGS[item.sex].nameButton.activeStateColor}
                isGenderNeutral={item.gender_neutral}
              />
            )

            nameIndex.current = nameIndex.current + 1

            if (
              (nameIndex.current > pmpIndex &&
                (nameIndex.current - pmpIndex) % inContentIndex === 0) ||
              (names.length < limit && index + 1 === names.length)
            ) {
              // push content to grid
              gridList.push(
                <ScNameGridList
                  key={`name-grid-${gridIndex++}`}
                  column={isMobile ? 1 : 2}
                  columnGap={'16px'}
                  rowGap={'12px'}
                >
                  {nameComponents.current}
                </ScNameGridList>
              )
              nameComponents.current = []
            } else if (nameIndex.current === pmpIndex) {
              // push content to grid
              gridList.push(
                <ScNameGridList
                  key={`name-grid-${gridIndex++}`}
                  column={isMobile ? 1 : 2}
                  columnGap={'16px'}
                  rowGap={'12px'}
                >
                  {nameComponents.current}
                </ScNameGridList>
              )
              nameComponents.current = []
            }
          })

          nameResult.current = [...nameResult.current, gridList]

          if (names.length < limit) {
            setIsComplete(true)
          }
        }
        setIsLoading(false)
        setIsReset(false)
      } catch (error) {
        console.log(`Error : ${error}`)
      }
    }
    // Fetch names when scroll to bottom
    if (inView && !debounce.current && !isComplete) {
      debounce.current = true

      fetchName()
      setTimeout(() => (debounce.current = false), 1000)

      offset.current = offset.current + 1
    }
  }, [
    gridIndex,
    gridList,
    inContentIndex,
    inView,
    isLoading,
    isComplete,
    isMobile,
    namePerRow,
    nameResult,
    offset,
    pmpIndex,
    searchParams,
    sex,
    text,
  ])

  return (
    <>
      {isReset && (
        <ScBGLoadingScreenWrapper>
          <LoadingScreen />
        </ScBGLoadingScreenWrapper>
      )}
      {renderedName}
      {isLoading && !isComplete && (
        <ScSearchResultLoadingScreenWrapper key={`loading-tail`}>
          <LoadingScreen mode="circular" />
        </ScSearchResultLoadingScreenWrapper>
      )}
    </>
  )
}

const SearchResultPage = ({ text, sex, isNeutral }) => {
  const { isMobile, isDesktop } = useContext(DetectDeviceContext)
  const [isHideModal, setIsHideModal] = useState(true)
  const [isInputValid, setIsInputValid] = useState(true)
  const [searchWidgetKey, setSearchWidgetKey] = useState('search-widget-key')

  useEffect(() => {
    // Reset state when the page was changed
    setIsHideModal(true)

    // validate if text contain any special charactor
    if (/[^A-Za-zÀ-ÖØ-öø-ÿ]/.test(text)) {
      setIsInputValid(false)
    } else {
      setIsInputValid(true)
    }
  }, [text, sex, isNeutral])

  const mainContent = (
    <div>
      {!isMobile && (
        <ScSearchWrapper>
          <SearchWidget key={searchWidgetKey} />
        </ScSearchWrapper>
      )}

      <ScAdSlotLeader>
        <AdSlot type="leaderboard_atf" cssClass="leaderboard" />
      </ScAdSlotLeader>

      {isInputValid ? (
        <ResultNameListRender text={text} sex={sex} isNeutral={isNeutral} />
      ) : (
        <ScTitle>No match found for ‘{text}’.</ScTitle>
      )}

      {!isHideModal && (
        <SearchModal
          onClose={() => {
            setIsHideModal(true)
            setSearchWidgetKey('search-widget-key-' + new Date().getTime())
          }}
        />
      )}
      {!isDesktop && (
        <ScStickyFlex>
          <StickyButton
            text="NAME"
            icon={<SearchIcon />}
            onClickFunc={() => setIsHideModal(false)}
          />
          <StickyButton text="SHARE" icon={<ShareIcon />} onClickFunc={() => webShareAPI()} />
        </ScStickyFlex>
      )}
    </div>
  )

  /* content aside (Desktop only) */
  const contentAside = isDesktop && (
    <ScContentAside>
      <ScAdSlotRightRail>
        <AdSlot type="med_rect_btf" />
      </ScAdSlotRightRail>
      {/* <RecommendedPosts limit={1} /> */}
      <ScSticky>
        <ScAdSlotRightRail>
          <AdSlot type="med_rect_btf" />
        </ScAdSlotRightRail>
      </ScSticky>
    </ScContentAside>
  )

  // 'Baby boy name' or 'Baby girl name' or 'Baby name'
  const typeOfResultComponent = ['Baby']
  SEX_CONFIGS[sex]?.slug && typeOfResultComponent.push(SEX_CONFIGS[sex].slug)
  typeOfResultComponent.push('name')
  const typeOfResult = typeOfResultComponent.join(' ')

  const pageTitle = `Search results for ${typeOfResult.toLowerCase()}: "${text}"`
  const pageDescriptionDynamic = `${typeOfResult} results for "${text}".`
  const pageDescriptionStatic = `Search Mom.com's boy, girl, and gender neutral baby name lists for inspiration on unique and meaningful baby names.`

  return (
    <Layout>
      <DocumentHead
        title={pageTitle}
        seoDescription={`${pageDescriptionDynamic} ${pageDescriptionStatic}`}
        ogURL={getOgURL(['sex', 'neutral'])}
      />
      <ScFlexWrapper>
        <PortionLayout
          mainSection={mainContent}
          subSection={contentAside}
          gridTemplateColumns={isDesktop ? '1fr 300px' : '1fr'}
          columnGap={'60px'}
        />
        {/* <ScTrendingWrapper>
          <TrendingStories title={'TRENDING STORIES'} />
        </ScTrendingWrapper> */}
      </ScFlexWrapper>
      <AdFooter />
    </Layout>
  )
}

SearchResultPage.propTypes = {
  text: PropTypes.string.isRequired,
  sex: PropTypes.string,
  isNeutral: PropTypes.bool,
}

SearchResultPage.defaultProps = {
  sex: null,
  isNeutral: false,
}

export default SearchResultPage
